/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/lib/stripeApi.ts
 * DESCRIPTION: Stripe API service layer for billing and subscription
 *              management. Handles checkout sessions, customer portal,
 *              and subscription status.
 * 
 * IMPORTS IN:
 * - src/pages/Billing.tsx
 * - src/components/Billing/CheckoutForm.tsx
 * 
 * DEPENDS ON:
 * - @stripe/stripe-js
 * - stripe
 * 
 * TODO: Connect to backend API for secure Stripe operations.
 *       Client-side Stripe integration is for demo purposes only.
 *       Production should use server-side Stripe API calls.
 * 
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2025-01-15
 * ─────────────────────────────────────────────────────────────────────
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.warn('Missing Stripe Publishable Key in environment variables');
}

/**
 * Initialize Stripe instance
 * @returns {Promise<Stripe | null>} Stripe instance or null
 */
export const initializeStripe = async (): Promise<Stripe | null> => {
  try {
    const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
    return stripe;
  } catch (error) {
    console.error('Failed to initialize Stripe:', error);
    return null;
  }
};

/**
 * Subscription plan configuration
 */
export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  period: 'month' | 'year';
  description: string;
  features: string[];
  stripePriceId?: string;
  popular?: boolean;
}

/**
 * Available subscription plans
 * TODO: Replace stripePriceId with actual Stripe Price IDs from dashboard
 */
export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'month',
    description: 'Perfect for trying out the platform',
    features: [
      '10 files per month',
      'Basic metadata extraction',
      'Email support',
      'API access (1,000 calls/mo)',
    ],
  },
  {
    id: 'pro',
    name: 'Business Pro',
    price: 99,
    period: 'month',
    description: 'Best for growing businesses',
    features: [
      '1,000 files per month',
      'Advanced metadata analysis',
      'Priority support',
      'API access (100,000 calls/mo)',
      'Team collaboration',
      'Custom webhooks',
      'Audit logs',
    ],
    stripePriceId: 'price_pro_monthly', // Replace with actual Stripe Price ID
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 299,
    period: 'month',
    description: 'For large-scale operations',
    features: [
      'Unlimited files',
      'AI-powered detection',
      '24/7 phone support',
      'Unlimited API calls',
      'Custom integrations',
      'SLA guarantee',
      'Dedicated account manager',
      'On-premise option',
    ],
    stripePriceId: 'price_enterprise_monthly', // Replace with actual Stripe Price ID
  },
];

/**
 * Mock subscription status for development
 * TODO: Replace with actual API call to backend
 */
export interface SubscriptionStatus {
  status: 'active' | 'canceled' | 'past_due' | 'trialing';
  currentPlan: SubscriptionPlan | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

/**
 * Mock subscription data for development
 */
const mockSubscriptionStatus: SubscriptionStatus = {
  status: 'active',
  currentPlan: SUBSCRIPTION_PLANS[1],
  currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  cancelAtPeriodEnd: false,
};

/**
 * Stripe API service
 */
export const stripeApi = {
  /**
   * Create checkout session for subscription
   * @param {string} planId - Subscription plan ID
   * @param {string} successUrl - URL to redirect after successful payment
   * @param {string} cancelUrl - URL to redirect after canceled payment
   * @returns {Promise<{ sessionId: string; url?: string }>} Checkout session data
   */
  createCheckoutSession: async (
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ sessionId: string; url?: string }> => {
    // TODO: Replace with actual backend API call
    // Example: const response = await api.post('/stripe/checkout', { planId, successUrl, cancelUrl });
    
    console.log('Creating checkout session for plan:', planId);
    
    // Mock implementation for development
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // In production, this would come from your backend
    return {
      sessionId: 'cs_test_mock_session_id',
      // For demo, redirect to success page
      url: successUrl,
    };
  },

  /**
   * Create customer portal session for managing subscription
   * @param {string} returnUrl - URL to redirect after portal session
   * @returns {Promise<{ url: string }>} Portal session URL
   */
  createPortalSession: async (returnUrl: string): Promise<{ url: string }> => {
    // TODO: Replace with actual backend API call
    // Example: const response = await api.post('/stripe/portal', { returnUrl });
    
    console.log('Creating portal session');
    
    // Mock implementation
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      url: 'https://billing.stripe.com/p/session/test_mock',
    };
  },

  /**
   * Get current subscription status
   * @returns {Promise<SubscriptionStatus>} Current subscription data
   */
  getSubscriptionStatus: async (): Promise<SubscriptionStatus> => {
    // TODO: Replace with actual API call to backend
    // Example: const response = await api.get('/stripe/subscription');
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return mockSubscriptionStatus;
  },

  /**
   * Cancel subscription at period end
   * @returns {Promise<boolean>} Cancellation success
   */
  cancelSubscription: async (): Promise<boolean> => {
    // TODO: Replace with actual backend API call
    console.log('Canceling subscription');
    await new Promise(resolve => setTimeout(resolve, 1000));
    return true;
  },

  /**
   * Get payment history/invoices
   * @returns {Promise<Array>} List of invoices
   */
  getInvoices: async () => {
    // TODO: Replace with actual API call to backend
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return [
      { id: 'inv_1', date: '2026-01-01', amount: 9900, status: 'paid', pdf: 'url' },
      { id: 'inv_2', date: '2026-12-03', amount: 9900, status: 'paid', pdf: 'url' },
      { id: 'inv_3', date: '2026-04-01', amount: 9900, status: 'paid', pdf: 'url' },
    ];
  },
};

export default stripeApi;
